import React from "react";
import numberFormatter from "utils/numberFormatter";
import styled from "styled-components/macro";

const SendNowConfirmationModalBodyText = "Selecting Send will trigger emails to go out immediately";

const alignmentOptions = {
  right: "right",
  center: "center",
  left: "left"
};

export const SendNowConfirmationModalConfig = {
  bodyText: SendNowConfirmationModalBodyText,
  title: "Send Mass Email Now",
  confirmationButtonText: "Send",
  cancellationButtonText: "Cancel",
  icon: "mail-arrow"
};

export const DEFAULT_COMPANY_DROPDOWN_OPTIONS = {
  id: -1,
  label: "All Companies"
};

// From config_items table with parentId 10a6c2f4-fd85-41da-a6bc-ee78c98e7dc4
const CAMPAIGN_TYPE_ID = {
  DEAL: "10b6c2f4-fd85-41da-a6bc-ee78c98e7dc4",
  PORTFOLIO: "10c6c2f4-fd85-41da-a6bc-ee78c98e7dc4",
  OTHER: "10d6c2f4-fd85-41da-a6bc-ee78c98e7dc4"
};

export const CAMPAIGN_VALUE_TYPE = {
  VALUATION_SENSITIVITY: "a8ef58c8-7619-466d-8860-fcc2c5ba4f4d",
  PREPAYMENT: "a7ef58c8-7619-466d-8860-fcc2c5ba4f4d",
  CASHOUT_PROCEEDS: "01870d97-5892-4b8f-aab1-94102b3213cb",
  REFI_BREAKEVEN: "4d595e8a-552d-4ba4-ad7e-0440b8872968",
  YOUR_DEBT_WITH_US: "a9ef58c8-7619-466d-8860-fcc2c5ba4f4d"
};

const CAMPAIGN_TYPE_NAME = {
  DEAL: "Deal",
  PORTFOLIO: "Portfolio",
  OTHER: "Other"
};

export const CAMPAIGN_TYPE = {
  DEAL: {
    id: CAMPAIGN_TYPE_ID.DEAL,
    name: CAMPAIGN_TYPE_NAME.DEAL,
    valueTypes: [CAMPAIGN_VALUE_TYPE.VALUATION_SENSITIVITY, CAMPAIGN_VALUE_TYPE.PREPAYMENT, CAMPAIGN_VALUE_TYPE.CASHOUT_PROCEEDS]
  },
  PORTFOLIO: {
    id: CAMPAIGN_TYPE_ID.PORTFOLIO,
    name: CAMPAIGN_TYPE_NAME.PORTFOLIO,
    valueTypes: [CAMPAIGN_VALUE_TYPE.REFI_BREAKEVEN, CAMPAIGN_VALUE_TYPE.YOUR_DEBT_WITH_US]
  },
  OTHER: {
    id: CAMPAIGN_TYPE_ID.OTHER,
    name: CAMPAIGN_TYPE_NAME.OTHER,
    valueTypes: []
  }
};

export const PORTFOLIO_STEPS = [
  {
    id: 0,
    label: "Review contact list",
    isDisabled: false
  },
  {
    id: 1,
    label: "Compose email",
    isDisabled: false
  },
  {
    id: 2,
    label: "Schedule email",
    isDisabled: false
  }
];

export const DEAL_STEPS = ({ isDisabled = true }) => [
  {
    id: 0,
    label: "Deal selection",
    isDisabled: true
  },
  {
    id: 1,
    label: "Compose email",
    isDisabled: false
  },
  {
    id: 2,
    label: "Review and send",
    isDisabled
  },
  {
    id: 3,
    label: "Email recipient details",
    isDisabled: false
  }
];

const DealName = styled.p`
  font-family: Work Sans;
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0em;
  color: #000000;
`;

const getActiveStyling = activeForCampaign => ({ opacity: `${activeForCampaign ? "1" : "0.25"}` });
const getTextAlignment = (textAlign = "center") => ({ textAlign });

const BASE_HEADERS = [
  {
    name: "Company Name",
    property: "companyName",
    headerAlignment: alignmentOptions.left,
    formatter: (_property, { sponsorCompany, activeForCampaign }) => (
      <DealName style={{ ...getActiveStyling(activeForCampaign), ...getTextAlignment(alignmentOptions.left) }}>{sponsorCompany?.name ?? ""}</DealName>
    )
  },
  {
    name: "Deal Name",
    property: "dealName",
    headerAlignment: alignmentOptions.left,
    formatter: (_property, { name, activeForCampaign }) => (
      <DealName style={{ ...getActiveStyling(activeForCampaign), ...getTextAlignment(alignmentOptions.left) }}>{name}</DealName>
    )
  },
  {
    name: "Broker Contact",
    property: "brokerContact",
    headerAlignment: alignmentOptions.left,
    formatter: (_property, { brokerContact, activeForCampaign }) => (
      <DealName style={{ ...getActiveStyling(activeForCampaign), ...getTextAlignment(alignmentOptions.left) }}>{brokerContact?.fullName ?? ""}</DealName>
    )
  }
];

export const PREPAYMENT_HEADERS = [
  ...BASE_HEADERS,
  {
    name: "Prepayment Penalty (%)",
    property: "prepaymentPercent",
    headerAlignment: alignmentOptions.left,
    formatter: (_property, { prepayment, activeForCampaign }) => (
      <span style={{ ...getActiveStyling(activeForCampaign), ...getTextAlignment(alignmentOptions.left) }}>
        {numberFormatter.formatRate(prepayment?.prepaymentPercent, 2, 0, false)}
      </span>
    )
  },
  {
    name: "Prepayment Penalty ($)",
    property: "prepaymentPenalty",
    headerAlignment: alignmentOptions.left,
    formatter: (_property, { prepayment, activeForCampaign }) => (
      <span style={{ ...getActiveStyling(activeForCampaign), ...getTextAlignment(alignmentOptions.left) }}>
        {numberFormatter.formatMoney(prepayment?.prepaymentPenalty)}
      </span>
    )
  }
];

export const VALUATION_HEADERS = [
  ...BASE_HEADERS,
  {
    name: "OPB ($)",
    property: "principalBalance",
    headerAlignment: alignmentOptions.left,
    formatter: (_property, { valuationSensitivity, activeForCampaign }) => {
      return (
        <span style={{ ...getActiveStyling(activeForCampaign), ...getTextAlignment(alignmentOptions.left) }}>{numberFormatter.formatMoney(valuationSensitivity.opb)}</span>
      );
    }
  },
  {
    name: "Valuation Range",
    property: "valuationRange",
    headerAlignment: alignmentOptions.left,
    formatter: (_property, { valuationSensitivity, activeForCampaign }) => {
      let displayStr;
      if (valuationSensitivity && typeof valuationSensitivity.noiMin === 'string') {
        displayStr = '--';
      } else {
        displayStr = `${numberFormatter.maskCurrency(valuationSensitivity.noiMin)} - ${numberFormatter.maskCurrency(valuationSensitivity.noiMax)}`;
      }
      return (
        <span style={{ ...getActiveStyling(activeForCampaign), ...getTextAlignment(alignmentOptions.left) }}>{displayStr}</span>
      );
    }

  }
];

const filterDealsArray = (data, searchTerm, companyId) => {
  const dealFilterByCompanyId = data.filter(({ sponsorCompanyId }) => (companyId >= 0 ? sponsorCompanyId === companyId : true));

  if (searchTerm) {
    const lowerSearchTerm = searchTerm?.toLowerCase();

    return dealFilterByCompanyId.filter(({ name }) => {
      const lowerName = name?.toLowerCase();

      return lowerName?.indexOf(lowerSearchTerm) >= 0;
    });
  }

  return dealFilterByCompanyId;
};

export const getCampaignTypesConfig = campaignType => {
  switch (campaignType) {
    case CAMPAIGN_VALUE_TYPE.PREPAYMENT:
      return {
        headers: PREPAYMENT_HEADERS,
        searchText: "Search deals by name",
        searchFunction: filterDealsArray
      };
    case CAMPAIGN_VALUE_TYPE.VALUATION_SENSITIVITY:
      return {
        headers: VALUATION_HEADERS,
        searchText: "Search deals by name",
        searchFunction: filterDealsArray
      };
    default:
      return {};
  }
};
